// Quo messaging — client-side view of a Quo call-summary row.
//
// The server stores the structured payload on member_chats.metadata
// (kind: "quo_call"); this parses it defensively so a malformed row still
// renders as its plain text. Mirrors QuoCallMeta in
// server/services/quo-messaging.ts.

export interface QuoCallView {
  callId: string;
  direction: "incoming" | "outgoing";
  status: string;
  durationSec: number | null;
  conversationUrl: string | null;
  summaryStatus: string | null;
  summary: string[];
  nextSteps: string[];
  voicemail: { transcript: string | null; recordingUrl: string | null; durationSec: number | null } | null;
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === "string" && s.trim().length > 0) : [];
}

export function parseQuoCallMeta(metadata: unknown): QuoCallView | null {
  if (!metadata || typeof metadata !== "object") return null;
  const m = metadata as Record<string, unknown>;
  if (m.kind !== "quo_call" || typeof m.callId !== "string") return null;
  const vm = m.voicemail && typeof m.voicemail === "object" ? (m.voicemail as Record<string, unknown>) : null;
  return {
    callId: m.callId,
    direction: m.direction === "outgoing" ? "outgoing" : "incoming",
    status: typeof m.status === "string" ? m.status : "completed",
    durationSec: typeof m.durationSec === "number" ? m.durationSec : null,
    conversationUrl: typeof m.conversationUrl === "string" && /^https:\/\//.test(m.conversationUrl) ? m.conversationUrl : null,
    summaryStatus: typeof m.summaryStatus === "string" ? m.summaryStatus : null,
    summary: stringList(m.summary),
    nextSteps: stringList(m.nextSteps),
    voicemail: vm
      ? {
          transcript: typeof vm.transcript === "string" ? vm.transcript : null,
          recordingUrl: typeof vm.recordingUrl === "string" && /^https:\/\//.test(vm.recordingUrl) ? vm.recordingUrl : null,
          durationSec: typeof vm.durationSec === "number" ? vm.durationSec : null,
        }
      : null,
  };
}

export function describeQuoCallStatus(direction: "incoming" | "outgoing", status: string): string {
  const s = String(status ?? "").toLowerCase();
  if (s === "answered" || s === "completed" || s === "in-progress") return "Answered";
  if (s === "missed" || s === "no-answer" || s === "abandoned") return direction === "incoming" ? "Missed" : "No answer";
  if (s === "busy") return "Busy";
  if (s === "canceled") return "Canceled";
  if (s === "failed") return "Failed";
  if (s === "forwarded") return "Forwarded";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function isMissedQuoCall(view: QuoCallView): boolean {
  const s = String(view.status ?? "").toLowerCase();
  return s === "missed" || s === "no-answer" || s === "abandoned" || s === "busy" || s === "canceled" || s === "failed";
}

export function formatQuoCallDuration(sec: number | null): string | null {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}
